import { Button } from '@/shared/ui/button';
import { AlertTriangle, ChartArea, Copy, FileText, Globe, ImagePlus, Loader2, MapPin, Monitor, Network, Pencil, Plus, RefreshCcw, Route, SatelliteDish, Trash, TriangleAlert, Info, Router } from 'lucide-react';
import EmptyView from '@/shared/components/EmptyView';
import { RouterData, UserTerminal } from '@/shared/types/userTerminalTypes';
import { ServiceLine, ServiceLineStatus } from '@/shared/types/serviceLineTypes';
import TerminalTelemetrySection from '@/domains/service-lines/components/TerminalTelemetrySection';
import { useState, useRef } from 'react';
import AddTerminalToServiceLineDialog from '@/domains/service-lines/dialogs/AddTerminalToServiceLineDialog';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { useGetTerminalById, useRebootTerminal } from '@/domains/terminals/services/account-user-terminals';
import { useRemoveTerminalFromServiceLine } from '@/domains/service-lines/services/account-service-lines';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { useAccountL2VPNCircuits } from '@/domains/l2vpns/services/account-l2vpn-circuits';
import AddL2VPNCircuitToTerminalDialog from '@/domains/l2vpns/dialogs/AddL2VPNCircuitToTerminalDialog';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { TooltipTrigger, Tooltip, TooltipContent, TooltipProvider } from '@/shared/ui/tooltip';
import { getTerminalTypeTextColorClass, TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';
import { OrgType } from '@/shared/types/userRoleTypes';
import { useUserRoles } from '@/shared/hooks/userRoles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Separator } from '@/shared/ui/separator';
import { H3Map } from '@/shared/components/H3Map';
import TerminalNotesDialog from '@/domains/terminals/dialogs/TerminalNotesDialog';
import UploadImagesDialog from '@/domains/files/UploadImagesDialog';
import ImagePreviewDialog from '@/domains/files/ImagePreviewDialog';
import { useDeleteFile, useGetFiles } from '@/domains/files/services/files';
import { FileEntityResponseData, ResourceType } from '@/shared/types/fileTypes';
import { Tabs, TabsContent, TabsTrigger, TabsList } from '@/shared/ui/tabs';
import RouterCard from '@/domains/routers/components/RouterCard';
import { TerminalRouteDialog } from '@/domains/terminals/dialogs/TerminalRouteDialog';

interface DevicesSectionProps {
  serviceLine: ServiceLine;
  refetchServiceLine: () => void | Promise<void>;
}

export const DevicesSection = ({
  serviceLine,
  refetchServiceLine,
}: DevicesSectionProps) => {
  const { hasPermission } = usePermissions();
  const { isPlatformUser } = useUserRoles();

  const availableTerminals = serviceLine?.terminals ?? [];
  const selectedTerminalIdRef = useRef<string>(availableTerminals[0]?.userTerminalId ?? '');
  const { data: terminalData, isLoading: isLoadingTerminal, refetch: refetchTerminal } = useGetTerminalById(selectedTerminalIdRef.current ?? undefined, isPlatformUser);
  const selectedTerminalData = terminalData ?? null;

  const [isAddTerminalToServiceLineDialogOpen, setIsAddTerminalToServiceLineDialogOpen] = useState(false);
  const [isRemoveTerminalFromServiceLineDialogOpen, setIsRemoveTerminalFromServiceLineDialogOpen] = useState(false);
  const [isRebootDialogOpen, setIsRebootDialogOpen] = useState(false);
  const { data: availableL2VPNCircuits, isLoading: isLoadingL2VPNCircuits } = useAccountL2VPNCircuits(serviceLine?.product?.l2vpnSupported === true);
  const [isAddL2VPNCircuitToTerminalDialogOpen, setIsAddL2VPNCircuitToTerminalDialogOpen] = useState(false);
  const [isTerminalNotesDialogOpen, setIsTerminalNotesDialogOpen] = useState(false);
  const [isUploadImagesDialogOpen, setIsUploadImagesDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<FileEntityResponseData | null>(null);
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);

  const [isTerminalRouteDialogOpen, setIsTerminalRouteDialogOpen] = useState(false);

  const rebootTerminal = useRebootTerminal();
  const removeTerminalFromServiceLine = useRemoveTerminalFromServiceLine();
  const deleteFile = useDeleteFile();

  const {
    data: images = [],
    isLoading: isLoadingImages,
    isFetching: isFetchingImages,
  } = useGetFiles(
    ResourceType.USER_TERMINAL,
    selectedTerminalIdRef.current ?? '',
  );

  const confirmRebootTerminal = async () => {
    if (!selectedTerminalIdRef.current) return;

    try {
      await rebootTerminal.mutateAsync({
        terminalId: selectedTerminalIdRef.current,
      });
      showSuccess(SUCCESS.TERMINAL.REBOOTED);
      await refetchServiceLine();
      setIsRebootDialogOpen(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.REBOOT);
    }
  };

  const confirmRemoveTerminalFromServiceLine = async () => {
    if (!selectedTerminalIdRef.current || !serviceLine?.serviceLineNumber) return;

    try {
      await removeTerminalFromServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        terminalId: selectedTerminalIdRef.current,
      });
      showSuccess(SUCCESS.SERVICE_LINE.REMOVED_TERMINAL);
      await refetchServiceLine();
      selectedTerminalIdRef.current = availableTerminals.find(terminal => terminal.userTerminalId !== selectedTerminalIdRef.current)?.userTerminalId ?? '';
      setIsRemoveTerminalFromServiceLineDialogOpen(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.REMOVE_TERMINAL);
    }
  };

  const confirmDeleteImage = async () => {
    if (!selectedTerminalIdRef.current || !imageToDelete) return;

    try {
      await deleteFile.mutateAsync({
        resourceType: ResourceType.USER_TERMINAL,
        resourceId: selectedTerminalIdRef.current,
        fileId: imageToDelete.fileId,
      });
      if (previewImageId === imageToDelete.fileId) {
        setPreviewImageId(null);
      }
      showSuccess(SUCCESS.TERMINAL.DELETE_IMAGE);
      setImageToDelete(null);
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.DELETE_IMAGE);
    }
  };

  const canManageServiceLineTerminals = hasPermission(OrgType.TENANT, Feature.SERVICE_LINE_TERMINALS, Permission.MANAGE);

  const copyToClipboard = (text: string, label: string = 'Content') => {
    navigator.clipboard.writeText(text);
    showSuccess(`${label} copied to clipboard`);
  };

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between bg-muted p-4 rounded-t-lg">
        <div className="flex items-center gap-2">
          <SatelliteDish className="h-6 w-6" />
          <h2 className="text-2xl font-bold">Devices</h2>
        </div>
        <div className="flex items-center gap-2">
          {canManageServiceLineTerminals && (serviceLine?.terminals?.length ?? 0) < (serviceLine?.product?.maxNumberOfUserTerminals ?? 0) && (
            <Button
              variant="default"
              size="default"
              className="gap-2"
              onClick={() => setIsAddTerminalToServiceLineDialogOpen(true)}
              disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
            >
              <Plus className="h-4 w-4" />
              Add Device
            </Button>
          )}
        </div>
      </div>

      <div className="p-4">
        {availableTerminals.length > 0 ? (
          <div className="flex flex-col border border bg-background rounded-lg p-4 gap-4">
            {/* Terminal Selection */}
            <div className="flex flex-row items-center justify-between w-full gap-2">
              {availableTerminals.length > 0 && (
                <Select
                  value={selectedTerminalIdRef.current}
                  onValueChange={(value) => selectedTerminalIdRef.current = value}
                >
                  <SelectTrigger className="w-1/2 h-full" >
                    <SelectValue placeholder="Select a terminal" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTerminals.map((terminal) => (
                      <SelectItem key={terminal.userTerminalId} value={terminal.userTerminalId}>
                        <div className="flex flex-row items-center justify-between w-full gap-4 py-2">
                          <SatelliteDish className="h-4 w-4 text-primary" />
                          <div className="flex flex-col items-start text-left">
                            <span className="text-sm font-bold">Device ID: {terminal.userTerminalId}</span>
                            <span className="text-xs">
                              Kit: {terminal.kitSerialNumber} | Serial: {terminal.dishSerialNumber}
                            </span>
                            {terminal.type && (
                              <span className={`text-right text-xs ${getTerminalTypeTextColorClass(terminal.type)}`}>{TERMINAL_TYPE_INFO[terminal.type].value}</span>
                            )}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedTerminalData && (
                <div className="flex flex-row items-center justify-end gap-2">
                  {/* Active Alerts */}
                  {selectedTerminalData.activeAlerts && selectedTerminalData.activeAlerts?.length > 0 && selectedTerminalData.activeAlerts?.some(alert => alert && alert.message) && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center bg-amber-500 w-8 h-8 rounded-md">
                            <AlertTriangle className="h-4 w-4 text-white" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs uppercase bg-black text-white">
                          <ul>{selectedTerminalData.activeAlerts?.filter(alert => alert && alert.message).map((alert, index) => <li key={index}>{alert?.message?.split('_')?.join(' ')}</li>)}</ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {canManageServiceLineTerminals && serviceLine?.product?.l2vpnSupported === true && !isLoadingL2VPNCircuits && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setIsAddL2VPNCircuitToTerminalDialogOpen(true)}
                      disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
                    >
                      <Network className="h-4 w-4" />
                      L2VPN
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setIsRebootDialogOpen(true)}
                    disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.OFFLINE || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
                  >
                    {rebootTerminal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                    Reboot
                  </Button>
                  {/* Only show remove terminal button if no L2VPN circuits are assigned to the terminal */}
                  {canManageServiceLineTerminals && (selectedTerminalData?.l2VpnCircuits?.length ?? 0) === 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="gap-2"
                      onClick={() => setIsRemoveTerminalFromServiceLineDialogOpen(true)}
                      disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED || serviceLine.status === ServiceLineStatus.PAUSED}
                    >
                      {removeTerminalFromServiceLine.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Separator />
            {selectedTerminalData && (() => {
              const routers = selectedTerminalData.routers?.filter(router => router.routerId !== null) ?? [];
              const gridCols = routers.length > 0 ? 'grid-cols-3' : 'grid-cols-2';
              return (
                <Tabs defaultValue="telemetry" className="flex flex-col overflow-hidden space-y-4">
                  <TabsList className={`grid w-full max-w-4xl shrink-0 justify-center mx-auto ${gridCols}`}>
                    <TabsTrigger value="telemetry" className="flex items-center justify-center gap-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
                      <ChartArea className="h-4 w-4" />
                      Telemetry
                    </TabsTrigger>
                    <TabsTrigger value="additional-information" className="flex items-center justify-center gap-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
                      <Info className="h-4 w-4" />
                      Additional Information
                    </TabsTrigger>
                    {routers.length > 0 && isPlatformUser &&
                      <TabsTrigger value="routers" className="flex items-center justify-center gap-2 data-[state=active]:bg-foreground data-[state=active]:text-background">
                        <Router className="h-4 w-4" />
                        Routers
                      </TabsTrigger>
                    }
                  </TabsList>
                  <TabsContent value="telemetry">
                    {TelemetryTabView(selectedTerminalData, isPlatformUser)}
                  </TabsContent>
                  <TabsContent value="additional-information">
                    {AdditionalInformationTabView(selectedTerminalData, canManageServiceLineTerminals)}
                  </TabsContent>
                  {routers.length > 0 && (
                    <TabsContent value="routers">
                      {RoutersTabView(routers)}
                    </TabsContent>
                  )}
                </Tabs>
              );
            })()}
          </div>
        ) : (
          <EmptyView
            icon={Monitor}
            size="medium"
            title="No Terminals Assigned"
            description="This service line doesn't have any terminals assigned to it yet."
          />
        )}
      </div>

      {selectedTerminalData && availableL2VPNCircuits && !isLoadingL2VPNCircuits && (
        <AddL2VPNCircuitToTerminalDialog
          open={isAddL2VPNCircuitToTerminalDialogOpen}
          onOpenChange={setIsAddL2VPNCircuitToTerminalDialogOpen}
          onSuccess={refetchServiceLine}
          terminal={selectedTerminalData}
          availableCircuits={availableL2VPNCircuits ?? []}
        />
      )}

      {serviceLine?.serviceLineNumber && (
        <AddTerminalToServiceLineDialog
          open={isAddTerminalToServiceLineDialogOpen}
          onOpenChange={setIsAddTerminalToServiceLineDialogOpen}
          onSuccess={refetchServiceLine}
          serviceLine={serviceLine}
        />
      )}

      {selectedTerminalData && (
        <TerminalNotesDialog
          open={isTerminalNotesDialogOpen}
          onOpenChange={setIsTerminalNotesDialogOpen}
          onSuccess={refetchServiceLine}
          terminal={selectedTerminalData}
        />
      )}

      {selectedTerminalIdRef.current && (
        <UploadImagesDialog
          open={isUploadImagesDialogOpen}
          onOpenChange={setIsUploadImagesDialogOpen}
          resourceType={ResourceType.USER_TERMINAL}
          resourceId={selectedTerminalIdRef.current}
        />
      )}

      {selectedTerminalIdRef.current && (
        <TerminalRouteDialog
          open={isTerminalRouteDialogOpen}
          onOpenChange={setIsTerminalRouteDialogOpen}
          terminalId={selectedTerminalIdRef.current}
        />
      )}

      <ImagePreviewDialog
        open={!!previewImageId}
        onOpenChange={(open) => !open && setPreviewImageId(null)}
        images={images}
        imageId={previewImageId}
        title="Image(s) Preview"
      />

      <GenericAlertDialog
        open={!!imageToDelete}
        onOpenChange={(open) => {
          if (!open) setImageToDelete(null);
        }}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteImage}
        loading={deleteFile.isPending}
      />

      <GenericAlertDialog
        open={isRemoveTerminalFromServiceLineDialogOpen}
        onOpenChange={setIsRemoveTerminalFromServiceLineDialogOpen}
        title="Remove Terminal from Service Line"
        description="Are you sure you want to remove this terminal from the service line?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveTerminalFromServiceLine}
        loading={removeTerminalFromServiceLine.isPending}
      />

      <GenericAlertDialog
        open={isRebootDialogOpen}
        onOpenChange={setIsRebootDialogOpen}
        title="Reboot Terminal"
        description="Are you sure you want to reboot this terminal?"
        confirmLabel="Reboot"
        cancelLabel="Cancel"
        onConfirm={confirmRebootTerminal}
        loading={rebootTerminal.isPending}
      />
    </div>
  );

  function TelemetryTabView(terminal: UserTerminal, isPlatformUser: boolean) {
    return <div className="flex flex-col w-full space-y-4">
      {/* Terminal Telemetry */}
      <TerminalTelemetrySection terminalId={terminal.userTerminalId} />
      <Separator />
      {/* Location */}
      <div className="flex flex-col w-full border rounded-lg">
        <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-md">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h3 className="text-lg font-semibold">Location</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Location Compliance Violation */}
            {terminal.locationComplianceViolation === true && isPlatformUser && (
              <div className="flex flex-row items-center justify-start gap-2">
                <TriangleAlert className="h-4 w-4 text-red-500" />
                <p className="text-sm text-red-500 italic font-bold">This terminal's location is outside the service line's registered country.</p>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setIsTerminalRouteDialogOpen(true)}
              disabled={terminal.lastLocation === null || terminal.lastLocation === '0'}
            >
              <Route className="h-4 w-4" />
              Route
            </Button>
          </div>
        </div>
        {terminal.lastLocation && terminal.lastLocation !== '0' ? (
          <H3Map polygons={[{ h3CellId: terminal.lastLocation }]} selectedPolygon={null} />
        ) : (
          <div className='p-2'>
            <EmptyView
              icon={MapPin}
              size="medium"
              title="No Location Data Found"
              description="This terminal doesn't have any location data at the moment." />
          </div>
        )}
      </div>
    </div>;
  }

  function AdditionalInformationTabView(terminal: UserTerminal, canManageServiceLineTerminals: boolean) {
    return <div className="flex flex-col w-full space-y-4">
      {/* Public IPs */}
      {terminal.ipAllocation && (
        <div className="flex flex-col w-full border rounded-lg">
          <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-md">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <h3 className="text-lg font-semibold">Public IPs</h3>
            </div>
            <div className="flex items-center gap-2">
              {terminal.ipAllocation?.createdAt && (
                <p className="text-sm text-muted-foreground">
                  Last updated: {formatDateToLocaleString(terminal.ipAllocation?.createdAt ?? '')}
                </p>
              )}
            </div>
          </div>

          <div className="p-4">
            <div className="flex flex-row items-center justify-between border rounded-md gap-2 p-2">
              <div className="flex flex-row items-center gap-2">
                {terminal.ipAllocation?.ipv4 && (
                  <span className="text-sm p-2 bg-background rounded-md"><span className="font-bold">IPv4:</span> {`${terminal.ipAllocation?.ipv4.join(', ') || '-'}`}</span>
                )}
                {terminal.ipAllocation?.ipv6Ue && (
                  <span className="text-sm p-2 bg-background rounded-md"><span className="font-bold">IPv6 UE:</span> {`${terminal.ipAllocation?.ipv6Ue.join(', ') || '-'}`}</span>
                )}
                {terminal.ipAllocation?.ipv6Cpe && (
                  <span className="text-sm p-2 bg-background rounded-md"><span className="font-bold">IPv6 CPE:</span> {`${terminal.ipAllocation?.ipv6Cpe.join(', ') || '-'}`}</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard((terminal.ipAllocation?.ipv4.join(', ') || '') + '\n' + (terminal.ipAllocation?.ipv6Ue.join(', ') || '') + '\n' + (terminal.ipAllocation?.ipv6Cpe.join(', ') || ''), 'Public IPs')}
                className="h-8 w-8 p-0 flex-shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Images */}
      <div className="flex flex-col w-full border rounded-lg">
        <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-md">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-4 w-4 text-primary" />
            <h3 className="text-lg font-semibold">Images</h3>
            {isFetchingImages && !isLoadingImages && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="flex items-center gap-2">
            {canManageServiceLineTerminals && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setIsUploadImagesDialogOpen(true)}
                disabled={serviceLine.updateAllowed === false || serviceLine.status === ServiceLineStatus.SUSPENDED}
              >
                <Plus className="h-4 w-4" />
                Upload Images
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col w-full space-y-4 p-4">
          {isLoadingImages ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading images...
            </div>
          ) : images.length > 0 ? (
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
              {images.map((image) => (
                <li
                  key={image.fileId}
                  className="group relative overflow-hidden rounded-md border bg-muted"
                >
                  <button
                    type="button"
                    className="block w-full cursor-zoom-in"
                    onClick={() => setPreviewImageId(image.fileId)}
                    aria-label="View image"
                    disabled={deleteFile.isPending}
                  >
                    <img
                      src={image.url}
                      alt="Image"
                      className="h-36 w-full object-cover transition-opacity group-hover:opacity-90"
                      loading="lazy" />
                  </button>
                  {canManageServiceLineTerminals && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1.5 right-1.5 h-7 w-7 p-0 opacity-90"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageToDelete(image);
                      }}
                      disabled={deleteFile.isPending ||
                        serviceLine.updateAllowed === false ||
                        serviceLine.status === ServiceLineStatus.SUSPENDED}
                      aria-label="Delete image"
                    >
                      {deleteFile.isPending && imageToDelete?.fileId === image.fileId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyView
              icon={ImagePlus}
              size="medium"
              title="No images"
              description="Add site photos for this terminal (JPEG, PNG or WebP)." />
          )}
        </div>
      </div>
      <Separator />
      {/* Notes */}
      <div className="flex flex-col w-full border rounded-lg">
        <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-md">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <h3 className="text-lg font-semibold">Terminal Notes</h3>
          </div>
          <div className="flex items-center gap-2">
            {canManageServiceLineTerminals && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setIsTerminalNotesDialogOpen(true)}
              >
                {terminal.notes ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {terminal.notes ? 'Edit Notes' : 'Add Notes'}
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          {terminal.notes ? (
            <div className="flex flex-row items-center justify-between gap-2 border rounded-md p-2">
              <p className="text-sm break-all break-words overflow-wrap-anywhere flex-1">{terminal.notes}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(terminal.notes, 'Notes')}
                className="h-8 w-8 p-0 flex-shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <EmptyView
              icon={FileText}
              size="medium"
              title="No notes found"
              description="This terminal doesn't have any notes yet." />
          )}
        </div>
      </div>
    </div>;
  }

  function RoutersTabView(routers: RouterData[]) {
    return <div className="flex flex-col w-full space-y-4">
      {routers.map((router) => (
        <RouterCard key={router.routerId} router={router} refetchTerminal={refetchTerminal} />
      ))}
    </div>;
  }
};